// frontend/src/pages/builder/dataSettings.js
// Phase 3 step 7b-1b: the builder's data settings -- sort, row limit and empty
// periods -- applied on top of the fields-only spec. Pure and unit-tested.
// Sorts name the chart's own items (a grouping by field, or a value), which the
// server resolves to its columns (7b-1a); empty periods are filled by the
// server (6c-3a), so the embed follows saved settings with no extra code.
// 7d-2: every grouping is a sort target (a table sorts by any column) and a
// header click toggles the sort; the chart's Sort control and the table's
// headers share one setting, so they always agree.
import { measureLabel } from './builderSpec';
import { sameTarget } from '../../charts/tableOrder';

export const ROW_LIMITS = [10, 25, 50, 100, 500, 1000, 5000];
export const DEFAULT_LIMIT = 1000;            // mirrors DEFAULT_LIMIT in app/semantic/spec.py
export const EMPTY_PERIOD_MODES = [
  { id: 'zero', label: 'Zero' },
  { id: 'gap', label: 'Gap' },
  { id: 'connect', label: 'Connect' },
];
const ADDITIVE = ['count', 'count_distinct', 'sum'];

export const emptySettings = () => ({ sort: null, limit: null, emptyPeriods: null });

const measureTarget = (m) => (m.aggregation === 'count' ? { aggregation: 'count' } : { aggregation: m.aggregation, field: m.field });

/**
 * What the chart can be sorted by: the X field (kind 'x'), the other
 * groupings (kind 'dimension'), then each value (kind 'value'). Empty
 * without a grouping: a single row has no order.
 */
export function sortTargets(spec, fieldsByKey = {}) {
  if (!spec || !spec.dimensions.length) return [];
  const name = (d) => (fieldsByKey[d.field] && fieldsByKey[d.field].display_name) || d.field;
  return [
    ...spec.dimensions.map((d, i) => ({
      key: i === 0 ? 'x' : `d${i}`, kind: i === 0 ? 'x' : 'dimension', time: Boolean(d.grain),
      target: { field: d.field }, label: name(d),
    })),
    ...spec.measures.map((m, i) => ({ key: `v${i}`, kind: 'value', target: measureTarget(m), label: measureLabel(m, fieldsByKey) })),
  ];
}

/** The automatic order: dates in time order; anything else by the first value, high to low. */
export function autoSort(spec) {
  if (!spec || !spec.dimensions.length) return null;
  const x = spec.dimensions[0];
  if (x.grain) return { target: { field: x.field }, dir: 'asc' };
  return { target: measureTarget(spec.measures[0]), dir: 'desc' };
}

/** The user's sort while it still points at something in the chart, otherwise automatic. */
export function effectiveSort(spec, settings) {
  const chosen = settings && settings.sort;
  if (chosen && sortTargets(spec).some((t) => sameTarget(t.target, chosen.target))) return chosen;
  return autoSort(spec);
}

/** Which sort option is selected: 'auto' or a sortTargets key. */
export function selectedSortKey(spec, settings) {
  const chosen = settings && settings.sort;
  if (!chosen || effectiveSort(spec, settings) !== chosen) return 'auto';
  const t = sortTargets(spec).find((x) => sameTarget(x.target, chosen.target));
  return t ? t.key : 'auto';
}

/**
 * A header click (7d-2): the sorted column flips direction; another column
 * starts high to low for a value, oldest first for a date, A to Z otherwise.
 * Returns the new settings.sort, or the current one for a target not in the chart.
 */
export function toggleSort(spec, settings, target) {
  const t = sortTargets(spec).find((x) => sameTarget(x.target, target));
  if (!t) return (settings && settings.sort) || null;
  const current = effectiveSort(spec, settings);
  if (current && sameTarget(current.target, target)) return { target: t.target, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  return { target: t.target, dir: t.kind === 'value' ? 'desc' : 'asc' };
}

/** Empty periods matter on a time X axis or with a Group by; Zero only changes counts and sums. */
export function emptyPeriodsInfo(spec, settings) {
  const dims = (spec && spec.dimensions) || [];
  const relevant = dims.length >= 2 || Boolean(dims[0] && dims[0].grain);
  const zeroApplies = ((spec && spec.measures) || []).some((m) => ADDITIVE.includes(m.aggregation));
  const chosen = settings && settings.emptyPeriods;
  let effective = chosen || (zeroApplies ? 'zero' : 'gap');
  if (effective === 'zero' && !zeroApplies) effective = 'gap';
  return { relevant, zeroApplies, effective };
}

/** "Top 10 by Count of records" when a value sort and a limit make a ranking, else null. */
export function rankingNote(spec, settings, fieldsByKey = {}) {
  const limit = settings && settings.limit;
  const sort = effectiveSort(spec, settings);
  if (!limit || limit >= DEFAULT_LIMIT || !sort || !sort.target.aggregation) return null;
  const t = sortTargets(spec, fieldsByKey).find((x) => sameTarget(x.target, sort.target));
  return t ? `${sort.dir === 'desc' ? 'Top' : 'Bottom'} ${limit} by ${t.label}` : null;
}

/** The spec actually sent: fields + sort + limit + empty periods. */
export function applySettings(spec, settings) {
  if (!spec) return spec;
  const out = { ...spec };
  const sort = effectiveSort(spec, settings);
  if (sort) out.sort = [{ ...sort.target, dir: sort.dir }];
  if (settings && settings.limit) out.limit = settings.limit;
  if (settings && settings.emptyPeriods) out.empty_periods = settings.emptyPeriods;
  return out;
}